import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
import { AppEvent, Payment } from '@/hooks/useFirebaseQueries';
import { format, startOfWeek, addDays, startOfDay, addHours } from 'date-fns';

interface SalesData {
  name: string;
  income: number;
  revenue: number;
  profit: number;
}

interface Props {
  data: SalesData[];
  events?: AppEvent[];
  payments?: Payment[];
}

export const SalesOverviewChart = ({ data, events = [], payments = [] }: Props) => {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState<'today' | 'weekly' | 'yearly'>('yearly');

  const chartData = useMemo(() => {
    if (filter === 'yearly') return data;
    
    if (filter === 'weekly') {
      const now = new Date();
      const start = startOfWeek(now, { weekStartsOn: 1 }); // Monday
      const weeklyData = [];
      
      for (let i = 0; i < 7; i++) {
        const day = addDays(start, i);
        const name = format(day, 'EEE'); // Mon, Tue...
        let income = 0, revenue = 0, profit = 0;
        
        events.forEach(e => {
          const d = new Date(e.date);
          if (d.getDate() === day.getDate() && d.getMonth() === day.getMonth() && d.getFullYear() === day.getFullYear()) {
            const total = parseFloat(e.total?.toString() || e.cost?.toString() || '0');
            income += total;
            profit += total * 0.3;
          }
        });
        
        payments.forEach(p => {
          const d = new Date(p.paymentDate || (p as any).payment_date);
          if (d.getDate() === day.getDate() && d.getMonth() === day.getMonth() && d.getFullYear() === day.getFullYear()) {
            revenue += parseFloat(p.amount?.toString() || '0');
          }
        });
        
        weeklyData.push({ name, income, revenue, profit });
      }
      return weeklyData;
    }
    
    if (filter === 'today') {
      const now = new Date();
      const start = startOfDay(now);
      const todayData = [];
      
      for (let i = 0; i < 24; i += 6) {
        const blockStart = addHours(start, i);
        const name = `${i}:00`;
        let income = 0, revenue = 0, profit = 0;
        
        events.forEach(e => {
          const d = new Date(e.date);
          if (d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getHours() >= i && d.getHours() < i + 6) {
            const total = parseFloat(e.total?.toString() || e.cost?.toString() || '0');
            income += total;
            profit += total * 0.3;
          }
        });
        
        payments.forEach(p => {
          const d = new Date(p.paymentDate || (p as any).payment_date);
          if (d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getHours() >= i && d.getHours() < i + 6) {
            revenue += parseFloat(p.amount?.toString() || '0');
          }
        });
        
        todayData.push({ name, income, revenue, profit });
      }
      return todayData;
    }
    
    return data;
  }, [filter, data, events, payments]);

  return (
    <Card className="bg-white border-0 shadow-sm rounded-xl overflow-hidden h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2 border-b border-gray-50/50">
        <CardTitle className="text-lg font-bold text-[#1a1d2d]">{t('dashboard.sales_overview', 'Sales Overview')}</CardTitle>
        <div className="flex gap-2 hidden sm:flex">
          <button 
            onClick={() => setFilter('today')}
            className={`text-xs px-3 py-1 rounded-md font-medium transition-colors ${filter === 'today' ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {t('common.today', 'Today')}
          </button>
          <button 
            onClick={() => setFilter('weekly')}
            className={`text-xs px-3 py-1 rounded-md font-medium transition-colors ${filter === 'weekly' ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {t('common.weekly', 'Weekly')}
          </button>
          <button 
            onClick={() => setFilter('yearly')}
            className={`text-xs px-3 py-1 rounded-md font-medium transition-colors ${filter === 'yearly' ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {t('common.yearly', 'Yearly')}
          </button>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="flex justify-center gap-6 mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#5b61e2]"></div>
            <span className="text-sm font-semibold text-gray-600">{t('dashboard.total_income', 'Total Income')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#f4a1ec]"></div>
            <span className="text-sm font-semibold text-gray-600">{t('dashboard.total_revenue', 'Total Revenue')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#ffc3d9]"></div>
            <span className="text-sm font-semibold text-gray-600">{t('dashboard.average_profit', 'Average Profit')}</span>
          </div>
        </div>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#5b61e2" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#5b61e2" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f4a1ec" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#f4a1ec" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ffc3d9" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#ffc3d9" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis 
                dataKey="name" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 12, fill: '#888888' }} 
                dy={10} 
                tickFormatter={(value) => filter === 'yearly' ? t(`months.${value.toLowerCase()}`, value) : value} 
              />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#888888' }} tickFormatter={(value) => `$${value}`} />
              <Tooltip 
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                formatter={(value: number) => [`$${value}`, '']}
              />
              <Area type="monotone" dataKey="profit" stroke="#ffc3d9" strokeDasharray="5 5" strokeWidth={2} fillOpacity={1} fill="url(#colorProfit)" />
              <Area type="monotone" dataKey="revenue" stroke="#f4a1ec" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" />
              <Area type="monotone" dataKey="income" stroke="#5b61e2" strokeWidth={2} fillOpacity={1} fill="url(#colorIncome)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
